import React, { useEffect } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationStatus } from '../../services/notificationApi';
import {
  dispatchContractRequest,
  getContractRequestMetadata,
} from '../../utils/contractRequestNotification';
import {
  isSubscriptionRenewalNotification,
  openLandlordPackageManagement,
} from '../../utils/subscriptionRenewalNotification';
import { requestTenantOpenInvoice, isTenantRentDueNotification } from '../../utils/tenantNotifications';
import { formatNotificationListingCopy } from '../../utils/notificationListing';

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'Vừa xong';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} phút trước`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} giờ trước`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} ngày trước`;

  return date.toLocaleDateString('vi-VN');
};

const MobileNotificationsPanel: React.FC = () => {
  const { user } = useAuth();
  const isTenant = user?.accountType === 'tenant';
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAllAsRead,
    markAsRead,
  } = useNotifications();

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const refresh = () => {
      void fetchNotifications();
    };
    window.addEventListener('kcn:refresh-notifications', refresh);
    return () => window.removeEventListener('kcn:refresh-notifications', refresh);
  }, [fetchNotifications]);

  return (
    <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-black">Thông báo</h2>
          {unreadCount > 0 && (
            <span className="rounded-full bg-[#C4A35A] px-2 py-0.5 text-xs font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllAsRead()}
            className="text-xs font-medium text-[#C4A35A]"
          >
            Đánh dấu đã đọc
          </button>
        )}
      </div>

      <div className="max-h-[calc(100vh-220px)] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C4A35A] border-t-transparent" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-16 text-center text-sm text-black/50">
            {isTenant
              ? 'Chưa có thông báo tiền phòng'
              : 'Không có thông báo'}
          </div>
        ) : (
          notifications.map((notification) => {
            const isUnread = notification.status === NotificationStatus.UNREAD;
            const listingCopy = formatNotificationListingCopy(notification);

            return (
              <div
                key={notification.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (isUnread) {
                    void markAsRead(notification.id);
                  }
                  if (isTenant) {
                    if (isTenantRentDueNotification(notification)) {
                      requestTenantOpenInvoice();
                    }
                    return;
                  }
                  const metadata = getContractRequestMetadata(notification);
                  if (metadata) {
                    dispatchContractRequest({ ...metadata, notification_id: notification.id });
                    return;
                  }
                  if (isSubscriptionRenewalNotification(notification)) {
                    openLandlordPackageManagement();
                  }
                }}
                className={`border-b border-black/5 px-4 py-3 ${isUnread ? 'bg-[#C4A35A]/5' : ''}`}
              >
                <div className="mb-1 flex items-start justify-between gap-2">
                  <p className="flex-1 text-sm font-medium text-black">{listingCopy.title}</p>
                  {isUnread && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#C4A35A]" />}
                </div>
                <p className="mb-1 text-sm text-black/70">{listingCopy.message}</p>
                <p className="text-xs text-black/40">{formatTime(notification.created_at)}</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileNotificationsPanel;
